/**
 * Customer entity — customers.Customer (xsite/customers/models.py, serializers.py).
 *
 * Note: the list endpoint returns a leaner shape (CustomerSerializer) than
 * retrieve/create/update (CustomerDetailSerializer) — fields only present on
 * the detail shape are marked optional below.
 */

import { createResourceApi } from '../shared/resourceApi';
import { createResourceHooks } from '../shared/createResourceHooks';
import type { Rental } from './rental';

export interface CustomerStatistics {
  total_rentals: number;
  active_rentals: number;
  completed_rentals: number;
  total_revenue: number;
  avg_rental_value: number;
  rentals_last_30_days: number;
  rentals_last_90_days: number;
  first_rental_date?: string | null;
  latest_rental?: Rental | null;
  most_rented_items: Array<{ item__name: string; item__id: number; total_quantity: number; rental_count: number }>;
}

export interface Customer {
  id: number;
  company_name: string;
  company_id: string;
  resp_person: string;
  email: string;
  city: string;
  discount: number | string;
  // Detail-only fields (absent from list responses)
  street?: string;
  postal_code?: string;
  tax_id?: string;
  telephone?: string;
  mobile?: string;
  description?: string;
  statistics?: CustomerStatistics;
  current_rentals?: Rental[];
}

export type CustomerInput = Omit<Customer, 'id'>;

export const customerApi = createResourceApi<Customer, CustomerInput, Partial<CustomerInput>>(
  'customers/api/customers'
);

export const {
  keys: customerKeys,
  useList: useCustomers,
  useDetail: useCustomer,
  useCreate: useCreateCustomer,
  useUpdate: useUpdateCustomer,
  useRemove: useDeleteCustomer,
} = createResourceHooks(customerApi, {
  name: 'customers',
  model: 'customers.customer',
  detailRelatedModels: ['rentals.rental', 'rentals.rentalitem'],
});
